import type { ThunkDispatch } from "redux-thunk";
import type { AnyAction } from "redux";
import type {
  ArtShortSerializer,
  RandomQuoteSerializer,
  TypedArtSearchSerializer,
} from "@litres-libs/types";
import { rtkqApi } from "../api/rtkqApi";
import type { TEntryData } from "../api/rtkqApi";

export function patchArtIsFinished(
  artId: number,
  dispatch: ThunkDispatch<any, any, AnyAction>,
  entryData: TEntryData
) {
  return dispatch(
    rtkqApi.util?.updateQueryData(
      // @ts-ignore
      entryData.endpointName,
      entryData.originalArgs,
      (cache: any) => {
        let cacheData;
        if ("data" in cache && Array.isArray(cache.data)) {
          cacheData = cache.data;
        }
        // в dashboardApi данные по артам записываются в arts
        if ("arts" in cache && Array.isArray(cache.arts)) {
          cacheData = cache.arts;
        }

        // для следующего арта в серии dashboardApi
        if ("art" in cache && cache.art && cache.art.id === artId) {
          cache.art.is_finished = true;
        }

        if (cacheData) {
          cacheData.forEach((a: ArtShortSerializer) => {
            if (a.id === artId) {
              a.is_finished = true;
            }
          });
        }
      }
    )
  );
}

export function patchArtIsNotFinished(
  artId: number,
  dispatch: ThunkDispatch<any, any, AnyAction>,
  entryData: TEntryData
) {
  return dispatch(
    rtkqApi.util?.updateQueryData(
      // @ts-ignore
      entryData.endpointName,
      entryData.originalArgs,
      (cache: any) => {
        let cacheData;
        if ("data" in cache && Array.isArray(cache.data)) {
          cacheData = cache.data;
        }
        // в dashboardApi данные по артам записываются в arts
        if ("arts" in cache && Array.isArray(cache.arts)) {
          cacheData = cache.arts;
        }

        // для следующего арта в серии dashboardApi
        if ("art" in cache && cache.art && cache.art.id === artId) {
          cache.art.is_finished = false;
        }

        if (cacheData) {
          cacheData.forEach((a: ArtShortSerializer) => {
            if (a.id === artId) {
              a.is_finished = false;
            }
          });
        }
      }
    )
  );
}

export function patchArtInCart(
  artId: number | number[],
  dispatch: ThunkDispatch<any, any, AnyAction>,
  entryData: TEntryData
) {
  return dispatch(
    rtkqApi.util?.updateQueryData(
      // @ts-ignore
      entryData.endpointName,
      entryData.originalArgs,
      (cache: any) => {
        cache.data.forEach((a: ArtShortSerializer) => {
          if (Array.isArray(artId)) {
            if (artId.includes(a.id)) {
              a.in_user_cart_status = "in_cart";
            }
          } else if (a.id === artId) {
            a.in_user_cart_status = "in_cart";
          }
        });
      }
    )
  );
}

export function patchArtNotInCart(
  artId: number | number[],
  dispatch: ThunkDispatch<any, any, AnyAction>,
  entryData: TEntryData
) {
  return dispatch(
    rtkqApi.util?.updateQueryData(
      // @ts-ignore
      entryData.endpointName,
      entryData.originalArgs,
      (cache: any) => {
        cache.data.forEach((a: ArtShortSerializer) => {
          if (Array.isArray(artId)) {
            if (artId.includes(a.id)) {
              a.in_user_cart_status = "not_in_cart";
            }
          } else if (a.id === artId) {
            a.in_user_cart_status = "not_in_cart";
          }
        });
      }
    )
  );
}

export function patchArtAddToFolder(
  artId: number,
  folderIds: number[],
  dispatch: ThunkDispatch<any, any, AnyAction>,
  entryData: TEntryData
) {
  const newFolders = folderIds.map(id => ({
    folder_id: id,
    is_public: false,
  }));
  return dispatch(
    rtkqApi.util?.updateQueryData(
      // @ts-ignore
      entryData.endpointName,
      entryData.originalArgs,
      (cache: any) => {
        cache.data.forEach((a: ArtShortSerializer) => {
          if (artId === a.id) {
            if (a.in_folders?.length) {
              a.in_folders.push(...newFolders);
            } else {
              a.in_folders = [...newFolders];
            }
          }
        });
      }
    )
  );
}

export function patchArtRemoveFromFolder(
  artId: number,
  folderIds: number[],
  dispatch: ThunkDispatch<any, any, AnyAction>,
  entryData: TEntryData
) {
  return dispatch(
    rtkqApi.util?.updateQueryData(
      // @ts-ignore
      entryData.endpointName,
      entryData.originalArgs,
      (cache: any) => {
        cache.data.forEach((a: ArtShortSerializer) => {
          if (artId === a.id) {
            a.in_folders =
              folderIds.length && a.in_folders
                ? a.in_folders.filter(f => !folderIds.includes(f.folder_id))
                : [];
          }
        });
      }
    )
  );
}

export function patchArtAddToWishlist(
  artId: number | number[],
  dispatch: ThunkDispatch<any, any, AnyAction>,
  entryData: TEntryData
) {
  return dispatch(
    rtkqApi.util?.updateQueryData(
      // @ts-ignore
      entryData.endpointName,
      entryData.originalArgs,
      (cache: any) => {
        let cacheData;
        if ("data" in cache && Array.isArray(cache.data)) {
          cacheData = cache.data;
        }
        // в dashboardApi данные по артам записываются в arts
        if ("arts" in cache && Array.isArray(cache.arts)) {
          cacheData = cache.arts;
        }

        if (cacheData) {
          cacheData.forEach(
            (a: ArtShortSerializer | TypedArtSearchSerializer) => {
              const art = "instance" in a ? a.instance : a;
              if (Array.isArray(artId)) {
                if (artId.includes(art.id)) {
                  art.is_liked = true;
                }
              } else if (art.id === artId) {
                art.is_liked = true;
              }
            }
          );
        } else {
          // для следующего арта в серии dashboardApi
          if (
            "art" in cache &&
            cache.art &&
            ((Array.isArray(artId) && artId.includes(cache.art.id)) ||
              cache.art.id === artId)
          ) {
            cache.art.is_liked = true;
          }

          // для слайдера с цитатами dashboardApi
          if ("quotes" in cache && cache.quotes.length) {
            cache.quotes.forEach((quote: RandomQuoteSerializer) => {
              if (Array.isArray(artId)) {
                if (artId.includes(quote.art_id)) {
                  quote.art_is_liked = true;
                }
              } else if (quote.art_id === artId) {
                quote.art_is_liked = true;
              }
            });
          }
        }
      }
    )
  );
}

export function patchArtRemoveFromWishlist(
  artId: number,
  dispatch: ThunkDispatch<any, any, AnyAction>,
  entryData: TEntryData
) {
  return dispatch(
    rtkqApi.util?.updateQueryData(
      // @ts-ignore
      entryData.endpointName,
      entryData.originalArgs,
      (cache: any) => {
        let cacheData;
        if ("data" in cache && Array.isArray(cache.data)) {
          cacheData = cache.data;
        }
        // в dashboardApi данные по артам записываются в arts
        if ("arts" in cache && Array.isArray(cache.arts)) {
          cacheData = cache.arts;
        }

        if (cacheData) {
          cacheData.forEach(
            (a: ArtShortSerializer | TypedArtSearchSerializer) => {
              const art = "instance" in a ? a.instance : a;
              if (art.id === artId) {
                art.is_liked = false;
              }
            }
          );
        } else {
          // для следующего арта в серии dashboardApi
          if ("art" in cache && cache.art && cache.art.id === artId) {
            cache.art.is_liked = false;
          }

          // для слайдера с цитатами dashboardApi
          if ("quotes" in cache && cache.quotes.length) {
            cache.quotes.forEach((quote: RandomQuoteSerializer) => {
              if (Array.isArray(artId)) {
                if (artId.includes(quote.art_id)) {
                  quote.art_is_liked = false;
                }
              } else if (quote.art_id === artId) {
                quote.art_is_liked = false;
              }
            });
          }
        }
      }
    )
  );
}

export function patchRemoveArtFromList(
  artId: number | number[],
  dispatch: ThunkDispatch<any, any, AnyAction>,
  entryData: TEntryData
) {
  return dispatch(
    rtkqApi.util?.updateQueryData(
      // @ts-ignore
      entryData.endpointName,
      entryData.originalArgs,
      (cache: any) => {
        Object.assign(cache, {
          data: cache.data.filter((a: ArtShortSerializer) => {
            if (Array.isArray(artId)) {
              return !artId.includes(a.id);
            } else {
              return a.id !== artId;
            }
          }),
        });
      }
    )
  );
}
